// Insights API service
import { baseClient } from './baseClient';
import type {
  AIInsight,
  AIInsightCreate,
  Agent
} from '../types/api';

export class InsightsApi {
  private static instance: InsightsApi;

  private constructor() {}

  public static getInstance(): InsightsApi {
    if (!InsightsApi.instance) {
      InsightsApi.instance = new InsightsApi();
    }
    return InsightsApi.instance;
  }

  /**
   * Get all insights
   */
  async getInsights(): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>('/insights/');
  }

  /**
   * Get a single insight by ID
   */
  async getInsight(id: string): Promise<AIInsight> {
    return baseClient.get<AIInsight>(`/insights/${id}`);
  }

  /**
   * Create a new insight
   */
  async createInsight(insight: AIInsightCreate): Promise<AIInsight> {
    return baseClient.post<AIInsight>('/insights/', insight);
  }

  /**
   * Delete an insight
   */
  async deleteInsight(id: string): Promise<void> {
    return baseClient.delete<void>(`/insights/${id}`);
  }

  /**
   * Get insights by deal
   */
  async getInsightsByDeal(dealId: string): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>(`/insights/deals/${dealId}`);
  }

  /**
   * Get insights by contact
   */
  async getInsightsByContact(contactId: string): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>(`/insights/contacts/${contactId}`);
  }

  /**
   * Get insights by agent
   */
  async getInsightsByAgent(agent: Agent): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>(`/insights/agents/${agent}`);
  }

  /**
   * Get recent insights
   */
  async getRecentInsights(days: number = 7): Promise<AIInsight[]> {
    const insights = await this.getInsights();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return insights.filter(insight => 
      new Date(insight.created_at) >= cutoffDate
    );
  }

  /**
   * Get insights by date range
   */
  async getInsightsByDateRange(startDate: Date, endDate: Date): Promise<AIInsight[]> {
    const insights = await this.getInsights();
    
    return insights.filter(insight => {
      const insightDate = new Date(insight.created_at);
      return insightDate >= startDate && insightDate <= endDate;
    });
  }

  /**
   * Get deal review queue insights
   */
  async getDealReviewQueue(): Promise<any[]> {
    return baseClient.get<any[]>('/insights/deal-review-queue');
  }

  /**
   * Get actionable insights
   */
  async getActionableInsights(): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>('/insights/actionable');
  }

  /**
   * Get insights summary
   */
  async getInsightsSummary(): Promise<any> {
    return baseClient.get<any>('/insights/summary');
  }

  /**
   * Get insights by category
   */
  async getInsightsByCategory(category: string): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>(`/insights/categories/${category}`);
  }

  /**
   * Get insights statistics
   */
  async getInsightsStats(): Promise<any> {
    const insights = await this.getInsights();
    
    return {
      total: insights.length,
      byAgent: insights.reduce((acc, insight) => {
        acc[insight.generated_by_agent] = (acc[insight.generated_by_agent] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byDeal: insights.filter(i => i.deal_id).length,
      byContact: insights.filter(i => i.contact_id).length,
      recent: insights.filter(i => {
        const insightDate = new Date(i.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return insightDate >= weekAgo;
      }).length,
    };
  }

  /**
   * Generate new insights for a deal
   */
  async generateDealInsights(dealId: string): Promise<AIInsight[]> {
    return baseClient.post<AIInsight[]>(`/insights/generate/deals/${dealId}`);
  }

  /**
   * Generate new insights for a contact
   */
  async generateContactInsights(contactId: string): Promise<AIInsight[]> {
    return baseClient.post<AIInsight[]>(`/insights/generate/contacts/${contactId}`);
  }

  /**
   * Get insights trends
   */
  async getInsightsTrends(period: string = 'weekly'): Promise<any> {
    return baseClient.get<any>(`/insights/trends?period=${period}`);
  }

  /**
   * Get insights recommendations
   */
  async getInsightsRecommendations(): Promise<any[]> {
    return baseClient.get<any[]>('/insights/recommendations');
  }

  /**
   * Mark insight as read
   */
  async markInsightAsRead(id: string): Promise<void> {
    return baseClient.post<void>(`/insights/${id}/read`);
  }

  /**
   * Mark insight as actionable
   */
  async markInsightAsActionable(id: string): Promise<void> {
    return baseClient.post<void>(`/insights/${id}/actionable`);
  }

  /**
   * Get insights dashboard data
   */
  async getInsightsDashboard(): Promise<any> {
    return baseClient.get<any>('/insights/dashboard');
  }

  /**
   * Get insights by priority
   */
  async getInsightsByPriority(priority: 'high' | 'medium' | 'low'): Promise<AIInsight[]> {
    return baseClient.get<AIInsight[]>(`/insights/priority/${priority}`);
  }

  /**
   * Get insights alerts
   */
  async getInsightsAlerts(): Promise<any[]> {
    return baseClient.get<any[]>('/insights/alerts');
  }

  /**
   * Acknowledge insight alert
   */
  async acknowledgeInsightAlert(alertId: string): Promise<void> {
    return baseClient.post<void>(`/insights/alerts/${alertId}/acknowledge`);
  }
}

// Export singleton instance
export const insightsApi = InsightsApi.getInstance(); 